import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { isFounderSupportView, readFounderSupportSession } from '../utils/founderSupportSession';
import { AssistantDraftField, AssistantDraftSource, AssistantPageDraft, buildAssistantPageDraft } from '../utils/assistantPageDraft';

interface Publication { owner: symbol; scope: string; source: AssistantDraftSource; draft: AssistantPageDraft | null }
interface Publisher {
  identity: string | null;
  publish: (owner: symbol, source: AssistantDraftSource, values: Partial<Record<AssistantDraftField, string>>, reportId?: number) => void;
  clear: (owner: symbol) => void;
}
const Writer = createContext<Publisher | null>(null);
const Reader = createContext<Publication | null>(null);
const sourceForPath = (path: string): AssistantDraftSource | null => {
  if (/^\/concept-validator\/?$/.test(path)) return 'concept';
  if (/^\/impact-evaluator\/?$/.test(path)) return 'impact';
  if (/^\/wayfinder\/?$/.test(path)) return 'wayfinder';
  if (/^\/geo\/?$/.test(path)) return 'brand-watch';
  if (/^\/geo-analyzer\/?$/.test(path)) return 'brand-watch-url';
  if (/^\/safeguard\/?$/.test(path)) return 'safeguard';
  if (/^\/impact-wizard\/?$/.test(path)) return 'impact-engine';
  return null;
};

export const AssistantPageDraftProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const { user, isLoggedIn } = useAuth();
  const location = useLocation();
  const identity = isLoggedIn ? user?.canonicalUserId || user?.id : null;
  const identityKey = identity ? String(identity) : null;
  const source = identity && !readFounderSupportSession() && !isFounderSupportView() ? sourceForPath(location.pathname) : null;
  const scope = JSON.stringify([identity, location.key, location.pathname, location.search]);
  const [publication, setPublication] = useState<Publication | null>(null);
  // Writers do not subscribe to keystrokes from other forms or the Assistant.
  const publisher = useMemo<Publisher>(() => ({
    identity: identityKey,
    publish: (owner, candidateSource, values, reportId) => {
      if (!source || candidateSource !== source) return;
      if (!Object.values(values).some(value => value?.trim())) {
        setPublication(current => current?.owner === owner ? null : current);
        return;
      }
      setPublication({ owner, scope, source, draft: buildAssistantPageDraft(source, values, reportId) });
    },
    clear: owner => setPublication(current => current?.owner === owner ? null : current),
  }), [scope, source, identityKey]);
  useEffect(() => { setPublication(current => current?.scope === scope && source ? current : null); }, [scope, source]);
  const visible = source && publication?.scope === scope ? publication : null;
  return <Writer.Provider value={publisher}><Reader.Provider value={visible}>{children}</Reader.Provider></Writer.Provider>;
};

export function usePublishAssistantPageDraft(source: AssistantDraftSource, values: Partial<Record<AssistantDraftField, string>>, reportId?: number, enabled = true) {
  const publisher = useContext(Writer);
  const owner = useRef(Symbol('assistant-page-form')).current;
  const formIdentity = useRef<string | null>(null);
  const serialized = JSON.stringify(values);
  useEffect(() => {
    // A mounted form keeps its original owner even through logout/login.
    if (!formIdentity.current && publisher?.identity) formIdentity.current = publisher.identity;
    if (formIdentity.current !== publisher?.identity) {
      publisher?.clear(owner);
      return;
    }
    if (enabled) publisher?.publish(owner, source, JSON.parse(serialized), reportId);
    else publisher?.clear(owner);
  }, [publisher, owner, source, serialized, reportId, enabled]);
  useEffect(() => () => publisher?.clear(owner), [publisher, owner]);
}

export const useAssistantPageDraft = () => useContext(Reader);
